import React, { useEffect, useState } from "react";
import { Box, Flex, Image } from "theme-ui";
import { Link } from "react-router-dom";
import { getRecentActivity, RecentActivityData, RecentActivity as RecentActivityType } from "api/Dashboard";
import { themevals } from "theme/themevals";
import {
  getEntityLink,
  getEntityLabel,
  getModuleLink,
  getModuleLabel,
  isListViewActivity,
} from "helpers/activityLog";

const COMPONENT_HEIGHT = 500;

// Generate initials from user name
const getInitials = (name: string): string => {
  const parts = name.split(" ").filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
  }
  return parts[0]?.[0]?.toUpperCase() || "?";
};

interface ActivityItemProps {
  activity: RecentActivityType;
}

const ActivityItem: React.FC<ActivityItemProps> = ({ activity }) => {
  const entityLink = getEntityLink(activity.entityType, activity.entityId);
  const entityTypeLabel = getEntityLabel(activity.entityType);
  
  // Check if this is a list view (no entityType but has module)
  const isListView = isListViewActivity(activity.entityType, activity.module);
  const listLink = isListView ? getModuleLink(activity.module) : null;
  const listLabel = isListView ? getModuleLabel(activity.module) : null;
  
  return (
    <Flex
      sx={{
        alignItems: "center",
        gap: "12px",
        padding: "12px 0",
      }}
    >
      {/* Avatar */}
      {activity.userAvatar ? (
        <Image
          src={activity.userAvatar}
          sx={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            objectFit: "cover",
            flexShrink: 0,
          }}
        />
      ) : (
        <Flex
          sx={{
            width: "36px",
            height: "36px",
            borderRadius: "50%",
            backgroundColor: themevals.colors.info,
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "13px",
              fontWeight: 600,
              color: "#FFFFFF",
            }}
          >
            {getInitials(activity.userName)}
          </Box>
        </Flex>
      )}

      {/* Activity details */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "13px",
            color: "base_700",
            lineHeight: 1.4,
          }}
        >
          <Box as="span" sx={{ fontWeight: 600, color: "base_800" }}>
            {activity.userName}
          </Box>{" "}
          {activity.action}{" "}
          {/* Entity link (specific item) */}
          {activity.entityNumber && entityLink ? (
            <>
              {entityTypeLabel}{" "}
              <Link
                to={entityLink}
                style={{
                  color: "#1A1A1A",
                  textDecoration: "underline",
                  fontWeight: 600,
                }}
              >
                {activity.entityNumber}
              </Link>
            </>
          ) : activity.entityNumber ? (
            <>
              {entityTypeLabel}{" "}
              <Box as="span" sx={{ fontWeight: 600, color: "base_800" }}>
                {activity.entityNumber}
              </Box>
            </>
          ) : /* List view link */ isListView && listLink && listLabel ? (
            <>
              the{" "}
              <Link
                to={listLink}
                style={{
                  color: "#1A1A1A",
                  textDecoration: "underline",
                  fontWeight: 600,
                }}
              >
                {listLabel}
              </Link>
            </>
          ) : isListView && listLabel ? (
            <>
              the{" "}
              <Box as="span" sx={{ fontWeight: 600, color: "base_800" }}>
                {listLabel}
              </Box>
            </>
          ) : null}
        </Box>
        {/* What it was about and what changed, so a row is never a bare
            "updated" (2026-10-15). */}
        {(activity.context || activity.detail) && (
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "12px",
              color: "base_500",
              lineHeight: 1.4,
              marginTop: "2px",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
            title={[activity.context, activity.detail].filter(Boolean).join(" \u00b7 ")}
          >
            {[activity.context, activity.detail].filter(Boolean).join(" \u00b7 ")}
          </Box>
        )}
      </Box>

      {/* Timestamp */}
      <Box
        sx={{
          fontFamily: "Poppins, sans-serif",
          fontSize: "12px",
          color: "base_500",
          flexShrink: 0,
          whiteSpace: "nowrap",
        }}
      >
        {activity.relativeTime}
      </Box>
    </Flex>
  );
};

const RecentActivityFeed: React.FC = () => {
  const [activityData, setActivityData] = useState<RecentActivityData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      const data = await getRecentActivity(10);
      setActivityData(data);
      setLoading(false);
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <Box
        sx={{
          backgroundColor: "#FFFFFF",
          borderRadius: "6px",
          border: "1px solid",
          borderColor: themevals.colors.inputOutline,
          boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
          flex: ["1 1 100%", "0 1 calc(50% - 8px)"],
          minHeight: [`auto`, `${COMPONENT_HEIGHT}px`],
          display: "flex",
          flexDirection: "column",
        }}
      >
        <Box
          sx={{
            padding: "16px 20px",
            borderBottom: "1px solid",
            borderColor: themevals.colors.inputOutline,
            minHeight: "76px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box
            sx={{
              fontFamily: "Poppins, sans-serif",
              fontSize: "14px",
              fontWeight: 600,
              color: "base_800",
            }}
          >
            Recent Activity
          </Box>
        </Box>
        <Box sx={{ padding: "20px", flex: 1 }}>
          <Box sx={{ backgroundColor: "#F5F5F5", height: "100%", borderRadius: "4px" }} />
        </Box>
      </Box>
    );
  }

  const activities = activityData?.activities || [];

  return (
    <Box
      sx={{
        backgroundColor: "#FFFFFF",
        borderRadius: "6px",
        border: "1px solid",
        borderColor: themevals.colors.inputOutline,
        boxShadow: "0px 0px 12px 4px rgba(129, 129, 129, 0.07)",
        flex: ["1 1 100%", "0 1 calc(50% - 8px)"],
        minHeight: [`auto`, `${COMPONENT_HEIGHT}px`],
        display: "flex",
        flexDirection: "column",
      }}
    >
      <Box
        sx={{
          padding: "16px 20px",
          borderBottom: "1px solid",
          borderColor: themevals.colors.inputOutline,
          minHeight: "76px",
          display: "flex",
          alignItems: "center",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            fontFamily: "Poppins, sans-serif",
            fontSize: "14px",
            fontWeight: 600,
            color: "base_800",
          }}
        >
          Recent Activity
        </Box>
      </Box>

      <Box
        sx={{
          padding: "8px 20px",
          flex: 1,
          overflowY: "auto",
          minHeight: 0,
          "&::-webkit-scrollbar": {
            display: "none",
          },
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {activities.length === 0 ? (
          <Flex
            sx={{
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              color: "base_500",
              fontFamily: "Poppins, sans-serif",
            }}
          >
            No recent activity
          </Flex>
        ) : (
          activities.map((activity) => (
            <ActivityItem key={activity.id} activity={activity} />
          ))
        )}
      </Box>
    </Box>
  );
};

export default RecentActivityFeed;
